import { default as React } from 'react';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { DocsHelpIcon } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

export const ComponentTypeField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
  idSchema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const hasErrors = rawErrors && rawErrors.length > 0;
  const docsUrl = uiSchema?.['ui:options']?.docsUrl as string | undefined;

  // Get options from schema
  const options = (schema.enum || []) as string[];
  const enumNames = (schema.enumNames || options) as string[];

  const handleChange = (event: any) => {
    onChange(event.target.value as string);
  };

  const id = idSchema?.$id;
  const label = schema.title || 'Component Type';

  return (
    <FormControl
      fullWidth
      className={classes.root}
      error={hasErrors}
      variant="outlined"
    >
      <Box className={classes.labelContainer}>
        <InputLabel
          htmlFor={id}
          shrink
          sx={{ position: 'relative', transform: 'none' }}
        >
          {label}
          {required ? ' *' : ' (Optional)'}
        </InputLabel>
        {docsUrl && (
          <DocsHelpIcon
            docsUrl={docsUrl}
            tooltipText="Component type documentation"
          />
        )}
      </Box>
      <Select
        id={id}
        value={formData || schema.default || ''}
        onChange={handleChange}
        label={label}
        required={required}
      >
        {options.map((option: string, index: number) => (
          <MenuItem key={option} value={option}>
            {enumNames[index] || option}
          </MenuItem>
        ))}
      </Select>

      {hasErrors && (
        <FormHelperText className={classes.errorText}>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}
      {!hasErrors && schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const ComponentTypeFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ComponentTypeField',
    component: ComponentTypeField,
  }),
);
